/**
 * Pure derivations from the worker's SyncState for the chip and the band. Kept free of the
 * store so they can be tested in Node. The five chip states and their exact wording come
 * from CONTENT.md section 1; red is spent only on "masalah sinkron".
 */

import { jamWit } from '@satu/shared/dates/wit'
import type { SyncState } from '../../db/protocol'

export type ChipStatus = 'tersinkron' | 'menunggu' | 'menyinkronkan' | 'tidak_terhubung' | 'masalah'

export interface ChipView {
  status: ChipStatus
  /** The visible and announced text. */
  label: string
  /** The number shown beside the dot when the chip is compact (phones); null when none. */
  count: number | null
  /** True only in the masalah state: the dot takes --color-primary. */
  merah: boolean
  /** True while the worker is syncing: the dot breathes (the one sanctioned loop). */
  berjalan: boolean
}

export const OFFLINE_BAND_TEXT =
  'Tidak terhubung. Semua pekerjaan tersimpan di perangkat ini dan dikirim saat terhubung.'

/**
 * Connectivity as the shell shows it. The browser's own signal wins when it says offline;
 * once the worker has evidence of its own (a sync that ran or failed), its verdict wins.
 */
export function isConnected(sync: SyncState, online: boolean): boolean {
  if (!online) return false
  const buktiPekerja = sync.lastSyncAt !== null || sync.lastError !== null
  return buktiPekerja ? sync.connected : true
}

/** "10.42": hours and minutes with the Indonesian dot, in WIT whatever the device zone. */
export function formatJam(iso: string): string {
  return jamWit(iso)
}

export function chipView(sync: SyncState, online: boolean): ChipView {
  const menunggu = sync.pending
  if (sync.problems > 0) {
    return {
      status: 'masalah',
      label: `${sync.problems} masalah sinkron`,
      count: sync.problems,
      merah: true,
      berjalan: false,
    }
  }
  if (!isConnected(sync, online)) {
    return {
      status: 'tidak_terhubung',
      label: 'Tidak terhubung · perubahan disimpan di perangkat ini',
      count: menunggu > 0 ? menunggu : null,
      merah: false,
      berjalan: false,
    }
  }
  // Before the first sync of a session the worker has nothing to report yet; the run that
  // follows login is what the chip is waiting for, so it reads as syncing (dot still).
  if (sync.syncing || sync.lastSyncAt === null) {
    return {
      status: 'menyinkronkan',
      label: 'Menyinkronkan…',
      count: menunggu > 0 ? menunggu : null,
      merah: false,
      berjalan: sync.syncing,
    }
  }
  if (menunggu > 0) {
    return {
      status: 'menunggu',
      label: `${menunggu} perubahan menunggu dikirim`,
      count: menunggu,
      merah: false,
      berjalan: false,
    }
  }
  return {
    status: 'tersinkron',
    label: `Tersinkron pukul ${formatJam(sync.lastSyncAt)}`,
    count: null,
    merah: false,
    berjalan: false,
  }
}
